import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';

import { parseApiError } from '@saas-kit/lib';
import { ErrorBanner, InputField, SubmitButton } from '@saas-kit/ui';

import { AuthCard } from './AuthCard';
import { acceptInvite } from './api';
import { acceptInviteSchema, type AcceptInviteInput } from './schemas';

export function AcceptInvite(): JSX.Element {
  const { t } = useTranslation('auth');
  const { token } = useParams<{ token: string }>();
  const [serverError, setServerError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const form = useForm<AcceptInviteInput>({
    resolver: zodResolver(acceptInviteSchema),
    defaultValues: { full_name: '', password: '' },
  });

  if (!token) {
    return (
      <AuthCard title={t('accept_invite.title')}>
        <ErrorBanner>{t('accept_invite.missing_token')}</ErrorBanner>
      </AuthCard>
    );
  }

  const submit = form.handleSubmit(async (values) => {
    setServerError(null);
    try {
      await acceptInvite(token, values);
      setDone(true);
    } catch (e) {
      setServerError(parseApiError(e).i18nKey);
    }
  });

  if (done) {
    return (
      <AuthCard
        title={t('accept_invite.success_title')}
        footer={
          <Link to="/auth/login" className="font-medium text-primary hover:underline">
            {t('accept_invite.success_link')}
          </Link>
        }
      >
        <p className="text-sm text-foreground">{t('accept_invite.success_body')}</p>
      </AuthCard>
    );
  }

  const errs = form.formState.errors;
  return (
    <AuthCard title={t('accept_invite.title')} subtitle={t('accept_invite.subtitle')}>
      <form onSubmit={(e) => void submit(e)} className="space-y-4" noValidate>
        {serverError ? <ErrorBanner>{t(serverError)}</ErrorBanner> : null}
        <InputField
          label={t('fields.full_name')}
          autoComplete="name"
          placeholder={t('fields.full_name_placeholder')}
          {...form.register('full_name')}
          error={errs.full_name ? t('validation.required') : undefined}
        />
        <InputField
          label={t('fields.new_password')}
          type="password"
          autoComplete="new-password"
          placeholder={t('fields.password_placeholder')}
          {...form.register('password')}
          error={errs.password ? t('validation.password_min') : undefined}
        />
        <SubmitButton
          loading={form.formState.isSubmitting}
          label={t('accept_invite.submit')}
          loadingLabel={t('accept_invite.submitting')}
        />
      </form>
    </AuthCard>
  );
}
